import { DotsThreeVertical } from '@phosphor-icons/react';
import { Typography } from '@remarkable/ark-web';
import { useSearch } from '@tanstack/react-router';
import { clsx } from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import type { FleetDevice } from 'src/api/endpoints/cloudEnterprise.types';
import { Button, Tooltip } from 'src/components';
import { CopyButton } from 'src/components/CopyButton';
import { DeviceIcon } from 'src/components/DeviceIcon';
import { DeviceName } from 'src/components/DeviceName';
import { IS_DEVELOPMENT } from 'src/config';
import { formatShortDate } from 'src/utils/formatDate';
import { DeviceComplianceNotification } from './DeviceComplianceNotification';
import { DeviceMenu } from './DeviceMenu';
import { DeviceVerificationNotification } from './DeviceVerificationNotification';
import { fleetMemberRowHelpers } from './fleetMemberRowHelpers';
import { InfoWithLabel } from './InfoWithLabel';

export function DeviceListItemDetailed({
  device,
  className,
  userId,
  userEmail,
  ...props
}: {
  device: FleetDevice;
  userId: string;
  userEmail?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  const search = useSearch({ strict: false });
  const currentDevices = [...(search.devices ?? []), search.device].filter(
    Boolean
  ) as string[];
  const isHighlighted = fleetMemberRowHelpers.shouldHighlightDevice(
    search.filter,
    currentDevices,
    device
  );

  return (
    <div
      {...props}
      className={clsx(
        'rounded-sm flex w-full flex-col gap-16 rounded-[4px] p-16',
        {
          'bg-paper-3': isHighlighted,
        },
        className
      )}
      key={device.serialNumber}
    >
      <div className="flex w-full items-center gap-8">
        <DeviceIcon device={device} size={32} className="shrink-0" />

        <div className="flex flex-1 flex-col gap-4">
          <Typography variant="body-lg" className="my-0 truncate font-medium">
            <DeviceName device={device} />
          </Typography>
          <CopyButton
            className="-ml-4 !min-h-0 max-w-full truncate !px-4 !py-2 text-14 !font-normal"
            value={device.serialNumber}
            size="small"
            variant="tertiary-neutral"
          >
            {device.serialNumber}
          </CopyButton>
        </div>

        <div>
          {IS_DEVELOPMENT && (
            <DeviceMenu device={device} userId={userId} userEmail={userEmail}>
              <Button
                variant="tertiary-neutral"
                size="medium"
                className="!rounded data-[state=open]:bg-paper-3"
              >
                <DotsThreeVertical />
              </Button>
            </DeviceMenu>
          )}
        </div>
      </div>

      <div className="flex w-full flex-col gap-16">
        <ul className="grid shrink-0 grid-cols-1 gap-16 ls:grid-cols-2 lm:grid-cols-3">
          <li>
            <InfoWithLabel label="Last seen">
              <Tooltip
                title={formatShortDate(device.lastSeen, {
                  showTime: true,
                  showYear: true,
                })}
              >
                <span>
                  {formatDistanceToNow(new Date(device.lastSeen ?? ''), {
                    addSuffix: true,
                  })}
                </span>
              </Tooltip>
            </InfoWithLabel>
          </li>
          <li>
            <InfoWithLabel label="Version">
              <Tooltip title={device.osVersion}>
                <span>
                  Version <span>{device.softwareVersion}</span>
                </span>
              </Tooltip>
            </InfoWithLabel>
          </li>
          <li>
            <InfoWithLabel label="Passcode digits">
              {device.passcodeDigits ?? 'N/A'}
            </InfoWithLabel>
          </li>
        </ul>
      </div>

      <DeviceComplianceNotification device={device} darken={isHighlighted} />
      <DeviceVerificationNotification device={device} darken={isHighlighted} />
    </div>
  );
}
